import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Building2, Save, Image as ImageIcon, FileText, Download, Upload, Trash2 } from "lucide-react";

const EMPTY = {
  name: "", legal_form: "", vat_number: "", fiscal_code: "",
  address: "", city: "", zip_code: "", province: "", country: "Italia",
  phone: "", email: "", user_email: "", pec: "", website: "",
  sdi_code: "", iban: "", bank_name: "",
  rea: "", share_capital: "", logo_base64: "", notes: "",
  visura_filename: "", visura_base64: "",
};

const LEGAL_FORMS = ["SRL", "SRLS", "SPA", "SNC", "SAS", "Ditta Individuale", "Cooperativa", "Altro"];

export default function CompanyProfilePage() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const isSuperadmin = user?.role === "superadmin";

  useEffect(() => {
    if (isSuperadmin) return;
    api.get("/company")
      .then((r) => { setForm({ ...EMPTY, ...r.data }); setLoaded(true); })
      .catch(() => toast.error("Errore caricamento anagrafica"));
  }, [isSuperadmin]);

  // Super admin doesn't have a real company — redirect to the multi-company admin
  if (isSuperadmin) {
    return <Navigate to="/app/admin?tab=companies" replace />;
  }

  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo max 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const onVisura = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("La visura deve essere in PDF"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Visura max 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, visura_filename: file.name, visura_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const downloadVisura = () => {
    if (!form.visura_base64) return;
    const a = document.createElement("a");
    a.href = form.visura_base64;
    a.download = form.visura_filename || "visura.pdf";
    a.click();
  };

  const removeVisura = () => {
    if (!confirm("Vuoi rimuovere la visura camerale?")) return;
    setForm({ ...form, visura_filename: "", visura_base64: "" });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Ragione sociale obbligatoria"); return; }
    setSaving(true);
    try {
      await api.put("/company", form);
      toast.success("Anagrafica aggiornata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="p-12 text-[#757575]">Caricamento…</div>;

  return (
    <div className="p-4 md:p-12" data-testid="company-profile-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Impostazioni</div>
          <h1 className="font-display text-4xl">Anagrafica Azienda</h1>
          <p className="text-[#555] mt-1 text-sm">Dati legali, fiscali e di fatturazione elettronica</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-company-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] h-11 btn-glow">
          <Save className="w-4 h-4 mr-2"/> {saving ? "Salvataggio…" : "Salva modifiche"}
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Colonna sinistra: logo + identificazione */}
        <div className="card-smart p-6 space-y-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#757575]">
            <Building2 className="w-3.5 h-3.5 text-[#FF7A29]"/> Identificazione
          </div>
          <div className="flex flex-col items-center gap-3 py-4 border-b border-[#EDE5D8]">
            {form.logo_base64 ? (
              <img src={form.logo_base64} alt="Logo" className="w-28 h-28 object-contain rounded-xl border border-[#EDE5D8] bg-white" />
            ) : (
              <div className="w-28 h-28 rounded-xl bg-[#FFF0E3] flex items-center justify-center">
                <ImageIcon className="w-8 h-8 text-[#FF7A29]/60"/>
              </div>
            )}
            <label className="cursor-pointer">
              <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="company-logo-input"/>
              <span className="text-xs text-[#FF7A29] underline hover:text-[#E85D00]">Carica logo (max 500KB)</span>
            </label>
          </div>
          <div>
            <Label>Ragione sociale *</Label>
            <Input data-testid="company-name" required className="rounded-xl mt-1" value={form.name} onChange={up("name")} />
          </div>
          <div>
            <Label>Forma giuridica</Label>
            <Select value={form.legal_form || undefined} onValueChange={(v)=>setForm({...form, legal_form: v})}>
              <SelectTrigger data-testid="company-legal-form" className="rounded-xl mt-1"><SelectValue placeholder="Seleziona…"/></SelectTrigger>
              <SelectContent>
                {LEGAL_FORMS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Partita IVA</Label>
            <Input data-testid="company-vat" className="rounded-xl mt-1" value={form.vat_number} onChange={up("vat_number")} placeholder="IT12345678901"/>
          </div>
          <div>
            <Label>Codice Fiscale</Label>
            <Input data-testid="company-cf" className="rounded-xl mt-1" value={form.fiscal_code} onChange={up("fiscal_code")}/>
          </div>
          <div>
            <Label>REA</Label>
            <Input className="rounded-xl mt-1" value={form.rea} onChange={up("rea")} placeholder="MI-1234567"/>
          </div>
          <div>
            <Label>Capitale sociale</Label>
            <Input className="rounded-xl mt-1" value={form.share_capital} onChange={up("share_capital")} placeholder="10.000,00 €"/>
          </div>
        </div>

        {/* Colonna centrale: sede + contatti */}
        <div className="card-smart p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-[#757575]">Sede legale</div>
          <div><Label>Indirizzo</Label><Input className="rounded-xl mt-1" value={form.address} onChange={up("address")}/></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Label>Città</Label><Input className="rounded-xl mt-1" value={form.city} onChange={up("city")}/></div>
            <div><Label>Prov.</Label><Input className="rounded-xl mt-1 uppercase" maxLength={2} value={form.province} onChange={(e)=>setForm({...form, province: e.target.value.toUpperCase()})}/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>CAP</Label><Input className="rounded-xl mt-1" value={form.zip_code} onChange={up("zip_code")}/></div>
            <div><Label>Nazione</Label><Input className="rounded-xl mt-1" value={form.country} onChange={up("country")}/></div>
          </div>

          <div className="pt-4 border-t border-[#EDE5D8] text-xs uppercase tracking-widest text-[#757575]">Contatti</div>
          <div><Label>Telefono</Label><Input className="rounded-xl mt-1" value={form.phone} onChange={up("phone")}/></div>
          <div><Label>Email</Label><Input type="email" className="rounded-xl mt-1" value={form.email} onChange={up("email")}/></div>
          <div>
            <Label>Email utilizzatore / referente</Label>
            <Input
              type="email"
              data-testid="company-user-email"
              className="rounded-xl mt-1"
              value={form.user_email}
              onChange={up("user_email")}
              placeholder="es. mario.rossi@azienda.it"
            />
            <div className="text-[10px] text-[#757575] mt-1">Email della persona che utilizza l'app (referente HR/operativo). Diversa dalla mail aziendale generica.</div>
          </div>
          <div><Label>PEC</Label><Input type="email" className="rounded-xl mt-1" value={form.pec} onChange={up("pec")} placeholder="azienda@pec.it"/></div>
          <div><Label>Sito web</Label><Input className="rounded-xl mt-1" value={form.website} onChange={up("website")} placeholder="https://…"/></div>
        </div>

        {/* Colonna destra: fatturazione + note */}
        <div className="card-smart p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-[#757575]">Fatturazione elettronica</div>
          <div>
            <Label>Codice SDI</Label>
            <Input data-testid="company-sdi" className="rounded-xl mt-1 uppercase" maxLength={7} value={form.sdi_code} onChange={(e)=>setForm({...form, sdi_code: e.target.value.toUpperCase()})}/>
          </div>
          <div><Label>IBAN</Label><Input className="rounded-xl mt-1 uppercase" value={form.iban} onChange={(e)=>setForm({...form, iban: e.target.value.toUpperCase()})}/></div>
          <div><Label>Banca</Label><Input className="rounded-xl mt-1" value={form.bank_name} onChange={up("bank_name")}/></div>

          <div className="pt-4 border-t border-[#EDE5D8] text-xs uppercase tracking-widest text-[#757575] flex items-center gap-2">
            <FileText className="w-3.5 h-3.5 text-[#FF7A29]"/> Visura camerale
          </div>
          {form.visura_base64 ? (
            <div className="border border-[#EDE5D8] rounded-xl p-3 bg-[#FFFAF5] flex items-center gap-2" data-testid="visura-present">
              <FileText className="w-5 h-5 text-[#FF7A29] flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{form.visura_filename || "visura.pdf"}</div>
                <div className="text-xs text-[#757575]">PDF caricato</div>
              </div>
              <button type="button" onClick={downloadVisura} data-testid="visura-download" className="p-2 hover:bg-white rounded-lg" title="Scarica">
                <Download className="w-4 h-4 text-[#FF7A29]"/>
              </button>
              <button type="button" onClick={removeVisura} data-testid="visura-remove" className="p-2 hover:bg-white rounded-lg" title="Rimuovi">
                <Trash2 className="w-4 h-4 text-red-500"/>
              </button>
            </div>
          ) : (
            <label htmlFor="visura-upload" className="flex items-center gap-2 px-4 h-11 rounded-xl border border-dashed border-[#FF7A29] bg-[#FFF0E3] cursor-pointer text-sm text-[#FF7A29] hover:bg-[#FFE3CC]" data-testid="visura-upload-label">
              <Upload className="w-4 h-4"/> Carica visura PDF (max 5MB)
              <input id="visura-upload" type="file" accept="application/pdf" onChange={onVisura} className="hidden"/>
            </label>
          )}

          <div className="pt-4 border-t border-[#EDE5D8] text-xs uppercase tracking-widest text-[#757575]">Note</div>
          <Textarea className="rounded-xl min-h-32" value={form.notes} onChange={up("notes")} placeholder="Note interne, condizioni, etc."/>
        </div>
      </div>
    </div>
  );
}
